import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { PageShell } from '../../../components/leaves/PageShell';
import { CalendarDays } from 'lucide-react';
import { Card, CardContent, CardHeader } from '../../../components/ui/Card';
import ScheduleTemplateCard from './components/ScheduleTemplateCard';
import ScheduleCalendar from './components/ScheduleCalendar';
import CreateScheduleDrawer from './components/CreateScheduleDrawer';
import TemplateDetailsModal from './components/TemplateDetailsModal';
import { Spinner } from '../../../components/ui/Spinner';
import { format, addDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, eachDayOfInterval } from 'date-fns';
import { useScheduleTemplates, useScheduledShifts } from '../../../hooks/useScheduling';
import { FetchUsers } from '../../../api/General/account';
import { useSelector } from 'react-redux';
import { deleteScheduledShift, deleteOverride, deleteShiftSet } from '../../../api/Attendance/scheduling';
import { formatErrorMessage, notifyError } from '../../../utils/errors';
import { useTranslation } from 'react-i18next';

const WorkSchedules = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('week'); // 'week' or 'month'
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [filterType, setFilterType] = useState('template'); // 'template' or 'user'
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [templateFilterText, setTemplateFilterText] = useState(() => window.__templateFilter || '');
  const [userFilterText, setUserFilterText] = useState(() => window.__userFilter || '');
  const [templateView, setTemplateView] = useState('grid'); // 'list' | 'grid'
  const [selectedDepartment, setSelectedDepartment] = useState(''); // '' = all
  const [scheduleStatusFilter, setScheduleStatusFilter] = useState('active'); // 'active' | 'inactive' | 'all'
  const [showCreateDrawer, setShowCreateDrawer] = useState(false);
  const [showTemplateDetails, setShowTemplateDetails] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editingShift, setEditingShift] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [shiftsEnabled, setShiftsEnabled] = useState(false);
  const [didInitDefaultTemplateFilter, setDidInitDefaultTemplateFilter] = useState(false);

  useEffect(() => {
    window.__templateFilter = templateFilterText;
  }, [templateFilterText]);

  useEffect(() => {
    window.__userFilter = userFilterText;
  }, [userFilterText]);

  // Get current user's company from Redux store
  const company = useSelector((state) => state.user.company);
  const rtl = useSelector((state) => state.user.rtl);
  const userCompanyId = company?.id;

  // Calculate date range based on view mode
  const dateRange = useMemo(() => {
    if (viewMode === 'week') {
      const start = startOfWeek(selectedDate, { weekStartsOn: 1 });
      const end = endOfWeek(selectedDate, { weekStartsOn: 1 });
      return eachDayOfInterval({ start, end });
    } else {
      // Pad the month range to full weeks so month grid aligns with weekday headers
      // and API fetch includes shifts on visible outside-month cells.
      const start = startOfWeek(startOfMonth(selectedDate), { weekStartsOn: 1 });
      const end = endOfWeek(endOfMonth(selectedDate), { weekStartsOn: 1 });
      return eachDayOfInterval({ start, end });
    }
  }, [selectedDate, viewMode]);

  // Fetch real data from API
  const { templates: scheduleTemplates, loading: templatesLoading, refresh: refreshTemplates } = useScheduleTemplates();

  const templatesByStatus = useMemo(() => {
    const templates = Array.isArray(scheduleTemplates) ? scheduleTemplates : [];
    const mode = (scheduleStatusFilter || 'active').toLowerCase();

    return templates.filter((t) => {
      const status = (t?.status || 'Active');
      if (mode === 'all') return true;
      if (mode === 'inactive') return status === 'Inactive';
      return status === 'Active';
    });
  }, [scheduleTemplates, scheduleStatusFilter]);

  // On first load: default to the first Active recurring schedule so we don't fetch unfiltered shifts.
  useEffect(() => {
    if (didInitDefaultTemplateFilter) return;
    if (templatesLoading) return;

    const templates = Array.isArray(scheduleTemplates) ? scheduleTemplates : [];
    if (templates.length === 0) {
      // No templates: allow unfiltered shifts (small) rather than blocking.
      setDidInitDefaultTemplateFilter(true);
      setShiftsEnabled(true);
      return;
    }

    const activeTemplates = templates.filter(t => (t?.status || 'Active') === 'Active');
    const ordered = (activeTemplates.length > 0 ? activeTemplates : templates)
      .slice()
      .sort((a, b) => {
        const ad = new Date(a?.updatedOn || a?.createdOn || 0).getTime();
        const bd = new Date(b?.updatedOn || b?.createdOn || 0).getTime();
        return bd - ad;
      });

    const firstActive = ordered[0];
    if (firstActive && !selectedFilter) {
      if (activeTemplates.length === 0) setScheduleStatusFilter('all');
      setFilterType('template');
      setSelectedFilter(firstActive);
    }

    setDidInitDefaultTemplateFilter(true);
    setShiftsEnabled(true);
  }, [didInitDefaultTemplateFilter, templatesLoading, scheduleTemplates, selectedFilter]);

  // If the user changes status filter while filtering by template, keep a valid selected template.
  useEffect(() => {
    if (!didInitDefaultTemplateFilter) return;
    if (filterType !== 'template') return;
    if (scheduleStatusFilter === 'all') return;

    const selectedStatus = selectedFilter?.status || 'Active';
    const wantsInactive = scheduleStatusFilter === 'inactive';
    const mismatch = wantsInactive ? (selectedStatus !== 'Inactive') : (selectedStatus !== 'Active');
    if (!mismatch) return;

    const next = templatesByStatus?.[0] || null;
    setSelectedFilter(next);
  }, [didInitDefaultTemplateFilter, filterType, scheduleStatusFilter, selectedFilter, templatesByStatus]);

  const {
    shifts: scheduledShifts,
    loading: shiftsLoading,
    refresh: refreshShifts
  } = useScheduledShifts(dateRange, {
    linkedRecurringRuleId: filterType === 'template' ? selectedFilter?.linkedRecurringRuleId : undefined,
    shiftSetId: filterType === 'template' ? selectedFilter?.id : undefined,
    userId: filterType === 'user' ? selectedFilter?.id : undefined,
  }, { enabled: shiftsEnabled });

  // Load users from API for user filter (backend handles role-based filtering)
  useEffect(() => {
    const loadUsers = async () => {
      if (!userCompanyId) return;

      try {
        setUsersLoading(true);
        const usersResponse = await FetchUsers('Company', userCompanyId, false);
        console.log('Users response:', usersResponse);

        if (usersResponse && usersResponse.payload && Array.isArray(usersResponse.payload)) {
          const users = usersResponse.payload.map(user => {
            const dept = typeof user?.departmentName === 'string'
              ? user.departmentName
              : (typeof user?.department === 'string'
                ? user.department
                : (user?.department?.name || user?.department?.title || ''));
            return {
              id: user.id,
              name: user.fullName || user.userName || user.email || '',
              fullName: user.fullName,
              department: dept,
            };
          });
          console.log('Mapped users:', users);
          setAllUsers(users);
          // Expose for components that may need global access (e.g., details modal)
          window.__allUsers = users;
        } else {
          console.warn('No users in response payload:', usersResponse);
          setAllUsers([]);
        }
      } catch (err) {
        console.error('Error loading users for filter:', err);
        setAllUsers([]);
      } finally {
        setUsersLoading(false);
      }
    };

    loadUsers();
  }, [userCompanyId]);

  const departmentOptions = useMemo(() => {
    const depts = (allUsers || [])
      .map(u => {
        const dept = typeof u?.department === 'string'
          ? u.department
          : (u?.department?.name || u?.department?.title || '');
        return (dept || '').trim();
      })
      .filter(Boolean);
    return Array.from(new Set(depts)).sort((a, b) => a.localeCompare(b));
  }, [allUsers]);

  const handleCreateSchedule = () => {
    setEditingShift(null);
    setShowCreateDrawer(true);
  };

  const handleEditShift = (shift) => {
    setEditingShift(shift);
    setShowCreateDrawer(true);
  };

  const handleViewTemplateDetails = (template) => {
    setSelectedTemplate(template);
    setShowTemplateDetails(true);
  };

  const handleEditTemplate = (template) => {
    // Convert template to an "editing shift" format for the drawer
    const rule = template?.recurringRules?.[0];
    const assignedIds = template?.assignedUserIds || template?.assignedUsers?.map(u => (typeof u === 'object' ? u.id : u)) || [];

    const fmtMinutes = (mins) => {
      if (typeof mins !== 'number' || !Number.isFinite(mins)) return '';
      const h = Math.floor(mins / 60);
      const m = mins % 60;
      return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
    };

    const editingData = {
      origin: 'Rule',
      isVirtual: true,
      recurringShiftRuleId: template?.recurringRuleId || template?.linkedRecurringRuleId || rule?.id,
      shiftSetId: template?.id,
      ruleName: template?.name,
      shiftSetName: template?.name,
      startTime: rule ? `${Math.floor(rule.startTimeOfDayMinutes / 60).toString().padStart(2, '0')}:${(rule.startTimeOfDayMinutes % 60).toString().padStart(2, '0')}` : '',
      endTime: rule ? `${Math.floor(rule.endTimeOfDayMinutes / 60).toString().padStart(2, '0')}:${(rule.endTimeOfDayMinutes % 60).toString().padStart(2, '0')}` : '',
      breakStartTime: rule ? fmtMinutes(rule.breakStartTimeOfDayMinutes) : '',
      breakEndTime: rule ? fmtMinutes(rule.breakEndTimeOfDayMinutes) : '',
      breakDurationMinutes: (rule?.breakDurationMinutes != null && Number(rule.breakDurationMinutes) > 0)
        ? Number(rule.breakDurationMinutes)
        : null,
      date: rule?.startDateUtc?.split('T')[0] || '',
      assignedUsers: (
        template?.assignedUsers?.length > 0
          ? template.assignedUsers
          : (assignedIds.map(id => {
            const user = allUsers?.find(x => String(x.id) === String(id));
            const display = user?.fullName || user?.name || String(id);
            return { id, name: display, fullName: user?.fullName };
          }) || [])
      ) || [],
    };
    setEditingShift(editingData);
    setShowCreateDrawer(true);
  };

  const handleNavigateDate = (direction) => {
    if (viewMode === 'week') {
      setSelectedDate(direction === 'next' ? addDays(selectedDate, 7) : addDays(selectedDate, -7));
    } else {
      const newDate = new Date(selectedDate);
      newDate?.setMonth(selectedDate?.getMonth() + (direction === 'next' ? 1 : -1));
      setSelectedDate(newDate);
    }
  };

  const handleScheduleCreated = () => {
    // Refresh data after creating/updating a schedule
    refreshTemplates();
    refreshShifts();
  };

  const handleDeleteTemplate = async (template, options) => {
    try {
      const templateId = Number(template?.id);
      if (!templateId) return;

      await deleteShiftSet(templateId, { cascade: !!options?.cascade });

      // Close modal if open
      setShowTemplateDetails(false);
      setSelectedTemplate(null);

      refreshTemplates();
      refreshShifts();
    } catch (err) {
      console.error('Delete template failed:', err);
      const msg = formatErrorMessage(err, t('FailedToDeleteRecurringSchedule'));
      notifyError(err);
      alert(msg);
    }
  };

  const handleDeleteShift = async (shift) => {
    try {
      if (!shift?.id) return;
      const confirmMsg = t('Are you sure you want to delete this shift?');
      const ok = window.confirm(confirmMsg);
      if (!ok) return;

      if (shift?.origin === 'Manual' || shift?.origin === 'ManualOverride') {
        await deleteOverride(Number(shift.id));
      } else {
        await deleteScheduledShift(Number(shift.id));
      }

      refreshShifts();
    } catch (err) {
      console.error('Delete shift failed:', err);
      const msg = formatErrorMessage(err, t('Failed to delete shift'));
      notifyError(err);
      // Optionally surface inline message via alert for now
      alert(msg);
    }
  };

  // Show loading state
  if (templatesLoading && scheduledShifts.length === 0) {
    return (
      <PageShell
        title={t('Work Schedules')}
        description={t('Unified flexible scheduling with templates, recurring rules, and assignments')}
        icon={CalendarDays}
        accentColor="blue"
        maxWidth="1600px"
      >
        <div className="flex flex-col items-center justify-center h-64 gap-3">
          <Spinner size="lg" className="text-primary" />
          <div className="text-sm text-muted-foreground">{t('Loading schedule data...')}</div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell
      title={t('Work Schedules')}
      description={t('Unified flexible scheduling with templates, recurring rules, and assignments')}
      icon={CalendarDays}
      accentColor="blue"
      maxWidth="1600px"
      headerActions={
        <Button onClick={handleCreateSchedule} variant="outline" iconName="Plus" iconPosition="left">
          {t('Create Schedule')}
        </Button>
      }
    >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Sidebar - Filter by User */}
          <div className="lg:col-span-1">
            <Card variant="elevated">
              <CardContent className="p-4">
              <h4 className="text-md font-semibold text-foreground mb-3">{t('Filter by User')}</h4>
              {usersLoading ? (
                <div className="text-sm text-muted-foreground py-2">{t('Loading users...')}</div>
              ) : (
                <div>
                  {/* Department Picker */}
                  <div className="mb-2">
                    <label className="block text-xs text-muted-foreground mb-1">{t('Department')}</label>
                    <select
                      className="w-full px-3 py-2 border border-border rounded-md text-sm bg-card"
                      value={selectedDepartment}
                      onChange={(e) => {
                        const val = e.target.value || '';
                        setSelectedDepartment(val);
                      }}
                    >
                      <option value="">{t('All departments')}</option>
                      {departmentOptions.map((dept) => (
                        <option key={dept} value={dept}>{dept}</option>
                      ))}
                    </select>
                  </div>

                  <input
                    type="text"
                    placeholder={t('Search user by name, email, or department...')}
                    className="w-full px-3 py-2 border border-border rounded-md text-sm mb-2"
                    value={userFilterText}
                    onChange={(e) => {
                      const q = (e.target.value || '').toLowerCase();
                      setUserFilterText(q);
                    }}
                  />
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {(allUsers || [])
                      .filter(u => {
                        const dept = typeof u?.department === 'string'
                          ? u.department
                          : (u?.department?.name || u?.department?.title || '');

                        if (selectedDepartment && (dept || '') !== selectedDepartment) return false;

                        const q = userFilterText || '';
                        if (!q) return true;

                        return (u?.name || '').toLowerCase().includes(q)
                          || (u?.fullName || '').toLowerCase().includes(q)
                          || (dept || '').toLowerCase().includes(q);
                      })
                      .slice(0, 20)
                      .map((user) => (
                        <button
                          key={user?.id}
                          onClick={() => {
                            setFilterType('user');
                            setSelectedFilter(user);
                          }}
                          className={`w-full text-start px-3 py-2 rounded-md text-sm transition-colors ${selectedFilter?.id === user?.id && filterType === 'user'
                              ? 'bg-primary text-white'
                              : 'bg-muted text-foreground hover:bg-muted'
                            }`}
                        >
                          <div className="font-medium">{user?.name}</div>
                          <div className={`text-xs ${selectedFilter?.id === user?.id && filterType === 'user' ? 'text-white/80' : 'text-muted-foreground'
                            }`}>
                            {typeof user?.department === 'string'
                              ? user.department
                              : (user?.department?.name || user?.department?.title || '')}
                          </div>
                        </button>
                      ))}
                  </div>
                </div>
              )}

              {/* Clear Filter */}
              {selectedFilter && (
                <Button
                  variant="outline"
                  fullWidth
                  className="mt-4"
                  onClick={() => {
                    setSelectedFilter(null);
                    setFilterType('template');
                  }}
                >
                  {t('Clear Filter')}
                </Button>
              )}
              </CardContent>
            </Card>
          </div>
          <div className="lg:col-span-3" >
            {/* Main Content - Calendar View */}
            <div>
              <Card variant="elevated">
                {/* Calendar Controls */}
                <div className="px-6 py-4 border-b border-border">
                  <div className="flex items-center justify-between">
                    <div className="flex flex-wrap items-center gap-4">
                      <button
                        onClick={() => handleNavigateDate('prev')}
                        className="p-2 hover:bg-muted rounded-md transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                      >
                        <Icon name={rtl ? "ChevronRight" : "ChevronLeft"} size={20} />
                      </button>
                      <h3 className="text-lg font-semibold text-foreground">
                        {viewMode === 'week'
                          ? t('Week of {{date}}', { date: format(startOfWeek(selectedDate, { weekStartsOn: 1 }), 'MMM d, yyyy') })
                          : format(selectedDate, 'MMMM yyyy')}
                      </h3>
                      <button
                        onClick={() => handleNavigateDate('next')}
                        className="p-2 hover:bg-muted rounded-md transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                      >
                        <Icon name={rtl ? "ChevronLeft" : "ChevronRight"} size={20} />
                      </button>
                      <button
                        onClick={() => setSelectedDate(new Date())}
                        className="px-3 py-1 text-sm border border-border rounded-md hover:bg-muted transition-colors"
                      >
                        {t('Today')}
                      </button>
                    </div>

                    <div className="flex items-center gap-2">
                      {/* View Mode Toggle */}
                      <div className="flex items-center bg-muted rounded-md p-1">
                        <button
                          onClick={() => setViewMode('week')}
                          className={`px-3 py-1 text-sm font-medium rounded transition-colors ${viewMode === 'week' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                            }`}
                        >
                          {t('Week')}
                        </button>
                        <button
                          onClick={() => setViewMode('month')}
                          className={`px-3 py-1 text-sm font-medium rounded transition-colors ${viewMode === 'month' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                            }`}
                        >
                          {t('Month')}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Filter Display */}
                  {selectedFilter && (
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <span className="text-sm text-muted-foreground">{t('Viewing')}: </span>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary/10 text-primary">
                        {filterType === 'template' ? (
                          <>
                            <Icon name="Calendar" size={14} style={{ marginInlineEnd: '0.25rem' }} />
                            {selectedFilter?.name}
                          </>
                        ) : (
                          <>
                            <Icon name="User" size={14} style={{ marginInlineEnd: '0.25rem' }} />
                            {selectedFilter?.name}
                          </>
                        )}
                      </span>
                    </div>
                  )}
                </div>

                {/* Loading indicator for shift data */}
                {shiftsLoading && (
                  <div className="flex items-center gap-2 px-6 py-2 bg-muted/50 border-b border-border">
                    <Spinner size="sm" className="text-primary" />
                    <span className="text-xs text-muted-foreground">{t('Loading shifts...')}</span>
                  </div>
                )}

                {/* Calendar Grid */}
                <ScheduleCalendar
                  viewMode={viewMode}
                  dateRange={dateRange}
                  rtl={rtl}
                  shifts={scheduledShifts}
                  onEditShift={handleEditShift}
                  onCreateShift={(date) => {
                    setEditingShift({ date: format(date, 'yyyy-MM-dd') });
                    setShowCreateDrawer(true);
                  }}
                  onDeleteShift={handleDeleteShift}
                />
              </Card>
            </div>

            {/* Schedule Templates (aligned under calendar column) */}
            <div className="mt-6 gap-6">
              <Card variant="elevated"><CardContent className="p-4">
                <div className="mb-4">
                  <div className="mt-2 flex items-center justify-between gap-3">
                    <h3 className="text-lg font-semibold text-foreground">{t('RecurringSchedules')}</h3>
                    {/* Templates View Toggle */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">{t('View')}:</span>
                      <div className="flex items-center bg-muted rounded-md p-1">
                        <button
                          onClick={() => setTemplateView('grid')}
                          className={`px-3 py-1 text-sm font-medium rounded transition-colors ${templateView === 'grid' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                            }`}
                        >
                          {t('Grid')}
                        </button>
                        <button
                          onClick={() => setTemplateView('list')}
                          className={`px-3 py-1 text-sm font-medium rounded transition-colors ${templateView === 'list' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                            }`}
                        >
                          {t('List')}
                        </button>
                      </div>
                      <button
                        onClick={() => navigate('/attendance/work-schedules/recurring')}
                        className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                        title={t('MaximizeView', { defaultValue: 'Maximize view' })}
                        data-testid="recurring-schedules-maximize"
                      >
                        <Icon name="Maximize2" size={16} />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Template Filter */}
                <div className="mb-4">
                  <div className="flex flex-col sm:flex-row gap-2">
                    <select
                      className="w-full sm:w-44 px-3 py-2 border border-border rounded-md text-sm bg-card"
                      value={scheduleStatusFilter}
                      onChange={(e) => setScheduleStatusFilter(e?.target?.value || 'active')}
                    >
                      <option value="active">{t('Active', { defaultValue: 'Active' })}</option>
                      <option value="inactive">{t('Inactive', { defaultValue: 'Inactive' })}</option>
                      <option value="all">{t('All', { defaultValue: 'All' })}</option>
                    </select>
                    <input
                      type="text"
                      placeholder={t('FilterRecurringSchedulesByName')}
                      className="w-full px-3 py-2 border border-border rounded-md text-sm"
                      value={templateFilterText}
                      onChange={(e) => {
                        const val = e.target.value?.toLowerCase() ?? '';
                        setSelectedFilter(null);
                        setFilterType('template');
                        setTemplateFilterText(val);
                      }}
                    />
                  </div>
                </div>

                {/* Template List/Grid */}
                <div
                  className={
                    templateView === 'grid'
                      ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3'
                      : 'border border-border rounded-lg overflow-hidden divide-y divide-border'
                  }
                >
                  {templatesByStatus
                    ?.filter(tmp => {
                      const q = templateFilterText || '';
                      return q.length === 0 || (tmp?.name || '').toLowerCase().includes(q);
                    })
                    ?.sort((a, b) => {
                      const ad = new Date(a?.updatedOn || a?.createdOn || 0).getTime();
                      const bd = new Date(b?.updatedOn || b?.createdOn || 0).getTime();
                      return bd - ad;
                    })
                    ?.map((template) => (
                      <ScheduleTemplateCard
                        key={template?.id}
                        template={template}
                        isSelected={selectedFilter?.id === template?.id && filterType === 'template'}
                        variant={templateView === 'grid' ? 'grid' : 'list'}
                        onClick={() => {
                          setFilterType('template');
                          setSelectedFilter(template);
                        }}
                        onViewDetails={() => handleViewTemplateDetails(template)}
                      />
                    ))}
                </div>
              </CardContent></Card>
            </div>
          </div>
        </div>

      {/* Create/Edit Schedule Drawer */}
      <CreateScheduleDrawer
        isOpen={showCreateDrawer}
        onClose={() => {
          setShowCreateDrawer(false);
          setEditingShift(null);
        }}
        onScheduleCreated={handleScheduleCreated}
        editingShift={editingShift}
        templates={scheduleTemplates}
        users={allUsers}
        preSelectedUser={filterType === 'user' && selectedFilter ? selectedFilter : null}
      />

      {/* Template Details Modal */}
      <TemplateDetailsModal
        isOpen={showTemplateDetails}
        onClose={() => {
          setShowTemplateDetails(false);
          setSelectedTemplate(null);
        }}
        template={selectedTemplate}
        onEditTemplate={handleEditTemplate}
        onDeleteTemplate={handleDeleteTemplate}
      />
    </PageShell>
  );
};

export default WorkSchedules;